// repo/primary-care-connect/frontend/src/pages/Portal/ClinicAdmin/FamilyDoctors/ManageFamilyDoctorsPage.tsx

import React, { useEffect, useState } from "react";
import "./ManageFamilyDoctorsPage.css";

import AddFamilyDoctorModal, {
  CpsLookupResult,
} from "./components/AddFamilyDoctorModal";

import FamilyDoctorRow, {
  FamilyDoctorRowData,
} from "./components/FamilyDoctorRow";

import {
  clinicAdminApi,
  ApiError,
  ClinicAdminFamilyDoctorIntakeRule,
} from "../../../../api/clinicAdmin";
import {
  asString,
  asNumber,
  pickFirst,
} from "../../../../utils/clinicAdminHelpers";
import ConfirmDialog from "../components/ConfirmDialog";
import { Skeleton } from "../components/Skeleton";
import { useToast } from "../components/ToastContext";

type LookupItem = { id: number; name: string };

type DoctorDetailsPanelState = {
  open: boolean;
  loading: boolean;
  error: string | null;

  familyDoctorId: number | null;

  doctor: {
    familyDoctorId: number;
    fullName?: string | null;
    cpso?: string | null;
    gender?: string | null;
    email?: string | null;
    status?: string | null;

    activatedAt?: string | null;
    deactivatedAt?: string | null;

    acceptingNewPatients?: boolean | null;
    acceptingStatusUpdatedAt?: string | null;

    intakeNotes?: string | null;
    firstAppointmentBookingUrl?: string | null;
    declaredCapacity?: number | null;
    remainingCapacityOverride?: number | null;
    remainingCapacityUpdatedAt?: string | null;
  } | null;

  languages: LookupItem[];
  visitTypes: LookupItem[];
  coverageTypes: LookupItem[];
  intakeRules: ClinicAdminFamilyDoctorIntakeRule[];
};

function emptyPanel(): DoctorDetailsPanelState {
  return {
    open: false,
    loading: false,
    error: null,
    familyDoctorId: null,
    doctor: null,
    languages: [],
    visitTypes: [],
    coverageTypes: [],
    intakeRules: [],
  };
}

function normalizeCompositePayload(raw: any): {
  doctor: any | null;
  languages: LookupItem[];
  visitTypes: LookupItem[];
  coverageTypes: LookupItem[];
} {
  // Backend expanded endpoint returns: { doctor, languages, visitTypes, coverageTypes }
  // But older shape could be direct doctor row.
  const doc = raw?.doctor ? raw.doctor : raw;

  const languagesRaw = Array.isArray(raw?.languages) ? raw.languages : [];
  const visitTypesRaw = Array.isArray(raw?.visitTypes) ? raw.visitTypes : [];
  const coverageTypesRaw = Array.isArray(raw?.coverageTypes)
    ? raw.coverageTypes
    : [];

  const map = (arr: any[]) =>
    arr
      .map((x) => ({
        id: asNumber(
          x?.id ??
            x?.ID ??
            x?.LanguageID ??
            x?.VisitTypeID ??
            x?.CoverageTypeID
        ),
        name: asString(x?.name ?? x?.Name) || "—",
      }))
      .filter((x) => x.id > 0);

  return {
    doctor: doc || null,
    languages: map(languagesRaw),
    visitTypes: map(visitTypesRaw),
    coverageTypes: map(coverageTypesRaw),
  };
}

function asNullableNumber(v: unknown): number | null {
  if (v === null || v === undefined || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

function asBoolMaybe(v: any): boolean | null {
  if (v === true || v === false) return v;
  if (v === 1 || v === 0) return v === 1;

  const s = String(v ?? "").trim().toLowerCase();
  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no") return false;

  return null;
}

function formatDateMaybe(s: string | null | undefined) {
  if (!s) return "—";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString();
}

function daysSinceMaybe(s: string | null | undefined): number | null {
  if (!s) return null;

  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;

  const diffMs = Date.now() - d.getTime();
  if (Number.isNaN(diffMs)) return null;

  return Math.floor(diffMs / (24 * 60 * 60 * 1000));
}

function acceptingStatusHint(
  acceptingNewPatients: boolean | null | undefined,
  acceptingStatusUpdatedAt: string | null | undefined
): string | null {
  if (acceptingNewPatients !== true) return null;

  const daysSince = daysSinceMaybe(acceptingStatusUpdatedAt);

  if (daysSince === null) {
    return "Needs confirmation.";
  }

  if (daysSince >= 30) {
    return "Overdue for confirmation.";
  }

  if (daysSince >= 29) {
    return "Needs confirmation today.";
  }

  if (daysSince >= 23) {
    const daysLeft = 30 - daysSince;
    return `Needs confirmation soon (${daysLeft} day${
      daysLeft === 1 ? "" : "s"
    } left).`;
  }

  return null;
}

type AgeGroupKey =
  | "InfantUnder1"
  | "Children"
  | "Adults"
  | "Seniors65"
  | "Seniors75";

type AgeGroupPreference = "" | "Comfortable" | "StrongFit";

const AGE_GROUP_OPTIONS: Array<{ key: AgeGroupKey; label: string }> = [
  { key: "InfantUnder1", label: "Newborn / infant under 1" },
  { key: "Children", label: "Children" },
  { key: "Adults", label: "Adults" },
  { key: "Seniors65", label: "Seniors 65+" },
  { key: "Seniors75", label: "Seniors 75+" },
];

const CLINICAL_COMFORT_OPTIONS: Array<{ value: string; label: string }> = [
  { value: "ChronicDiseaseDiabetes", label: "Chronic disease / diabetes" },
  { value: "FrailSeniors", label: "Frail seniors" },
  { value: "MentalHealth", label: "Mental health" },
  { value: "NewbornCare", label: "Newborn care" },
  { value: "WomensHealth", label: "Women’s health" },
  { value: "PrenatalPostpartum", label: "Prenatal / postpartum" },
  { value: "PalliativeCare", label: "Palliative care" },
  {
    value: "DisabilityAccessibilityNeeds",
    label: "Disability / accessibility needs",
  },
];

function safeIntakeString(v: unknown): string | null {
  if (v === null || v === undefined) return null;
  const s = String(v).trim();
  return s.length ? s : null;
}

function parseIntakeJson(raw: string | null | undefined): unknown {
  const s = safeIntakeString(raw);
  if (!s) return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

function parseIntakeStringList(raw: string | null | undefined): string[] {
  const parsed = parseIntakeJson(raw);
  if (Array.isArray(parsed)) {
    return parsed.map((x) => String(x)).filter(Boolean);
  }

  const s = safeIntakeString(raw);
  if (!s) return [];

  return s
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

function normalizeAgePreference(value: unknown): AgeGroupPreference {
  const s = safeIntakeString(value);
  if (s === "Comfortable" || s === "Accept") return "Comfortable";
  if (s === "StrongFit" || s === "Prefer") return "StrongFit";
  return "";
}

function agePreferenceLabel(value: AgeGroupPreference): string {
  if (value === "Comfortable") return "Comfortable";
  if (value === "StrongFit") return "Strong fit";
  return "No preference";
}

function complexityLabel(value: unknown): string {
  const s = safeIntakeString(value);
  if (s === "StandardOnly") return "Standard complexity";
  if (s === "Moderate") return "Moderate complexity";
  if (s === "ComfortableWithComplex") {
    return "Comfortable with complex patients";
  }
  if (s === "DiscussComplexCasesFirst" || s === "AvoidHighComplexity") {
    return "Discuss complex cases with clinic first";
  }
  return "";
}

function geographyLabel(value: unknown): string {
  const s = safeIntakeString(value);
  if (s === "NoPreference") return "No geographic preference";
  if (s === "SameFSAPreferred") return "Same FSA preferred";
  if (s === "SameCityPreferred") return "Same city preferred";
  if (s === "ReasonableDistancePreferred") {
    return "Within reasonable driving distance preferred";
  }
  return "";
}

function interpretIntakeRules(rules: ClinicAdminFamilyDoctorIntakeRule[]) {
  const ageGroups: Record<AgeGroupKey, AgeGroupPreference> = {
    InfantUnder1: "",
    Children: "",
    Adults: "",
    Seniors65: "",
    Seniors75: "",
  };
  let complexity = "";
  let clinicalAreas: string[] = [];
  let geography = "";
  let navigatorNote = "";

  for (const rule of rules || []) {
    const key = safeIntakeString(rule?.criterionKey);
    if (!key) continue;

    if (key === "ageGroups") {
      const parsed = parseIntakeJson(rule.criterionValue);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        const record = parsed as Record<string, unknown>;
        for (const option of AGE_GROUP_OPTIONS) {
          ageGroups[option.key] = normalizeAgePreference(record[option.key]);
        }
      }
      continue;
    }

    if (key === "complexityComfort") {
      complexity = complexityLabel(rule.criterionValue);
      continue;
    }

    if (key === "clinicalComfortAreas") {
      clinicalAreas = parseIntakeStringList(rule.criterionValue)
        .map(
          (value) =>
            CLINICAL_COMFORT_OPTIONS.find((option) => option.value === value)
              ?.label
        )
        .filter((label): label is string => Boolean(label));
      continue;
    }

    if (key === "geographyPreference") {
      geography = geographyLabel(rule.criterionValue);
      continue;
    }

    if (key === "navigatorNote") {
      navigatorNote = safeIntakeString(rule.criterionValue) || "";
    }
  }

  const visibleAgeGroups = AGE_GROUP_OPTIONS.filter(
    (option) => ageGroups[option.key]
  ).map((option) => ({
    key: option.key,
    label: option.label,
    preference: agePreferenceLabel(ageGroups[option.key]),
  }));

  const hasAny =
    visibleAgeGroups.length > 0 ||
    !!complexity ||
    clinicalAreas.length > 0 ||
    !!geography ||
    !!navigatorNote.trim();

  return {
    ageGroups: visibleAgeGroups,
    complexity,
    clinicalAreas,
    geography,
    navigatorNote,
    hasAny,
  };
}

function httpBookingUrl(raw: string | null | undefined): string | null {
  const s = String(raw || "").trim();
  if (/^https?:\/\//i.test(s)) return s;
  return null;
}

function formatCapacity(value: number | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return String(value);
}

/**
 * v1 note:
 * FamilyDoctorRowData only has `id`.
 * - For Active/Inactive rows: id = FamilyDoctorID
 * - For Invited rows: id = ProviderInviteID (because FamilyDoctorID is NULL)
 */
function toRowData(item: any): FamilyDoctorRowData {
  // Accept multiple possible shapes/casing from backend
  const familyDoctorId = asNumber(
    pickFirst(item, ["familyDoctorId", "FamilyDoctorID", "FamilyDoctorId"])
  );

  const providerInviteId = asNumber(
    pickFirst(item, ["providerInviteId", "ProviderInviteID", "ProviderInviteId"])
  );

  // IMPORTANT: Invited rows have no FamilyDoctorID. Use ProviderInviteID instead.
  const id = familyDoctorId || providerInviteId || 0;

  const displayName =
    asString(
      pickFirst(item, ["fullName", "FullName", "displayName", "DisplayName"])
    ) || "—";

  const cpscNumber =
    asString(
      pickFirst(item, [
        "cpso",
        "CPSO",
        "cpscNumber",
        "cpsNumber",
        "CPSONumber",
      ])
    ) || "—";

  const email = asString(pickFirst(item, ["email", "Email"])) || "—";

  const gender =
    asString(
      pickFirst(item, ["gender", "Gender", "invitedGender", "InvitedGender"])
    ) || undefined;

  const statusRaw = asString(pickFirst(item, ["status", "Status"])) || "";

  const status: FamilyDoctorRowData["status"] =
    statusRaw === "Invited" || statusRaw === "Inactive" ? statusRaw : "Active";

  const invitedAt =
    asString(pickFirst(item, ["invitedAt", "InvitedAt"])) ?? undefined;

  return {
    id,
    displayName,
    cpscNumber,
    email,
    gender,
    status,
    invitedAt,
  };
}

export default function ManageFamilyDoctorsPage() {
  const [familyDoctors, setFamilyDoctors] = useState<FamilyDoctorRowData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [actionBusy, setActionBusy] = useState(false);
  const { showToast } = useToast();

  const [showAddModal, setShowAddModal] = useState(false);

  const [confirmState, setConfirmState] = useState<{
    message: string;
    title?: string;
    danger?: boolean;
    confirmLabel?: string;
    onConfirm: () => void;
  } | null>(null);

  function requestConfirm(opts: {
    message: string;
    title?: string;
    danger?: boolean;
    confirmLabel?: string;
    onConfirm: () => void;
  }) {
    setConfirmState(opts);
  }

  const [panel, setPanel] = useState<DoctorDetailsPanelState>(emptyPanel());

  const [acceptingStatusDraft, setAcceptingStatusDraft] =
    useState<boolean | null>(null);

  useEffect(() => {
    setAcceptingStatusDraft(panel.doctor?.acceptingNewPatients ?? null);
  }, [panel.doctor?.familyDoctorId, panel.doctor?.acceptingNewPatients]);

  async function refreshList() {
    const env = await clinicAdminApi.listFamilyDoctors();

    // Most likely: { items: [...] }
    // Safety: allow other shapes just in case.
    const items =
      (Array.isArray((env as any)?.items) && (env as any).items) ||
      (Array.isArray((env as any)?.recordset) && (env as any)?.recordset) ||
      [];

    const rows = (items as any[])
      .map(toRowData)
      // keep only valid IDs (prevents rendering garbage rows)
      .filter((r: FamilyDoctorRowData) => Number.isFinite(r.id) && r.id > 0);

    setFamilyDoctors(rows);
  }

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        setError(null);

        await refreshList();
      } catch (e: any) {
        if (!mounted) return;

        if (e instanceof ApiError && e.status === 403) {
          setError("You are not authorized to view providers for this clinic.");
        } else {
          setError(e?.message || "Failed to load family doctors.");
        }
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, []);

  async function runAction(fn: () => Promise<void>, successMsg: string) {
    try {
      setActionBusy(true);

      await fn();
      await refreshList();

      showToast(successMsg, "success");
    } catch (e: any) {
      const msg =
        e instanceof ApiError
          ? e.message || "Action failed."
          : e?.message || "Action failed.";
      showToast(msg, "error");
    } finally {
      setActionBusy(false);
    }
  }

  async function openDoctorPanel(familyDoctorId: number) {
    if (!Number.isFinite(familyDoctorId) || familyDoctorId <= 0) return;
    if (actionBusy) return;

    setPanel({
      open: true,
      loading: true,
      error: null,
      familyDoctorId,
      doctor: null,
      languages: [],
      visitTypes: [],
      coverageTypes: [],
      intakeRules: [],
    });

    try {
      const raw = await clinicAdminApi.getFamilyDoctor(familyDoctorId);

      const normalized = normalizeCompositePayload(raw);

      const d = normalized.doctor || {};
      const doctor = {
        familyDoctorId:
          asNumber(d?.familyDoctorId ?? d?.FamilyDoctorID ?? familyDoctorId) ||
          familyDoctorId,
        fullName: asString(d?.fullName ?? d?.FullName) ?? null,
        cpso: asString(d?.cpso ?? d?.CPSO ?? d?.Cpso) ?? null,
        gender: asString(d?.gender ?? d?.Gender) ?? null,
        email: asString(d?.email ?? d?.Email) ?? null,
        status: asString(d?.status ?? d?.Status) ?? null,

        activatedAt: asString(d?.activatedAt ?? d?.ActivatedAt) ?? null,
        deactivatedAt: asString(d?.deactivatedAt ?? d?.DeactivatedAt) ?? null,

        acceptingNewPatients: asBoolMaybe(
          d?.acceptingNewPatients ?? d?.AcceptingNewPatients
        ),
        acceptingStatusUpdatedAt:
          asString(
            d?.acceptingStatusUpdatedAt ?? d?.AcceptingStatusUpdatedAt
          ) ?? null,

        intakeNotes: asString(d?.intakeNotes ?? d?.IntakeNotes) ?? null,
        firstAppointmentBookingUrl: asString(
          d?.firstAppointmentBookingUrl ?? d?.FirstAppointmentBookingUrl
        ),
        declaredCapacity: asNullableNumber(
          d?.declaredCapacity ?? d?.DeclaredCapacity
        ),
        remainingCapacityOverride: asNullableNumber(
          d?.remainingCapacityOverride ?? d?.RemainingCapacityOverride
        ),
        remainingCapacityUpdatedAt: asString(
          d?.remainingCapacityUpdatedAt ?? d?.RemainingCapacityUpdatedAt
        ),
      };

      setPanel((prev) => ({
        ...prev,
        open: true,
        loading: false,
        error: null,
        doctor,
        languages: normalized.languages,
        visitTypes: normalized.visitTypes,
        coverageTypes: normalized.coverageTypes,
        intakeRules: Array.isArray(raw?.intakeRules) ? raw.intakeRules : [],
      }));
    } catch (e: any) {
      setPanel((prev) => ({
        ...prev,
        open: true,
        loading: false,
        error: e?.message || "Failed to load doctor details.",
      }));
    }
  }

  function closeDoctorPanel() {
    setPanel(emptyPanel());
  }

  async function updateAcceptingStatus() {
    const doctor = panel.doctor;
    const familyDoctorId = doctor?.familyDoctorId;
    if (doctor?.status !== "Active") return;
    if (!familyDoctorId) return;
    if (actionBusy) return;
    if (acceptingStatusDraft === null) return;
    if (acceptingStatusDraft === doctor.acceptingNewPatients) return;

    try {
      setActionBusy(true);

      const result = await clinicAdminApi.setFamilyDoctorAcceptingStatus(
        familyDoctorId,
        acceptingStatusDraft
      );

      setPanel((prev) => {
        if (
          !prev.doctor ||
          prev.doctor.familyDoctorId !== familyDoctorId ||
          result.familyDoctorId !== familyDoctorId
        ) {
          return prev;
        }

        return {
          ...prev,
          doctor: {
            ...prev.doctor,
            acceptingNewPatients: result.acceptingNewPatients,
            acceptingStatusUpdatedAt: result.acceptingStatusUpdatedAt,
          },
        };
      });

      showToast("Accepting status updated.", "success");
    } catch (e: any) {
      const msg =
        e instanceof ApiError
          ? e.message || "Action failed."
          : e?.message || "Action failed.";
      showToast(msg, "error");
    } finally {
      setActionBusy(false);
    }
  }

  const intakePreferences = interpretIntakeRules(panel.intakeRules);
  const bookingUrl = panel.doctor?.firstAppointmentBookingUrl || "";
  const bookingHref = httpBookingUrl(bookingUrl);

  return (
    <div className="clinic-admin-providers">
      <header className="providers-header">
        <div>
          <h1>Manage Family Doctors</h1>
          <p className="subtitle">
            Invite and manage family doctors with portal access to your clinic.
          </p>
        </div>

        <button
          className="primary-btn"
          onClick={() => setShowAddModal(true)}
          disabled={actionBusy}
          title={actionBusy ? "Please wait…" : undefined}
        >
          + Add Family Doctor
        </button>
      </header>

      {error ? <div className="providers-error">{error}</div> : null}

      <div className="providers-table-wrapper">
        <table className="providers-table">
          <thead>
            <tr>
              <th>Family Doctor</th>
              <th>CPSO #</th>
              <th>Email</th>
              <th>Status</th>
              <th className="actions-col">Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <>
                {[1, 2, 3, 4].map((i) => (
                  <tr key={i}>
                    <td>
                      <Skeleton width="120px" height="14px" />
                    </td>
                    <td>
                      <Skeleton width="70px" height="14px" />
                    </td>
                    <td>
                      <Skeleton width="150px" height="14px" />
                    </td>
                    <td>
                      <Skeleton
                        width="60px"
                        height="14px"
                        borderRadius="999px"
                      />
                    </td>
                    <td>
                      <Skeleton width="80px" height="14px" />
                    </td>
                  </tr>
                ))}
              </>
            ) : (
              <>
                {familyDoctors.map((fd) => (
                  <FamilyDoctorRow
                    key={`${fd.status}-${fd.id}`}
                    familyDoctor={fd}
                    onOpenDetails={() => {
                      // Only Active/Inactive have a real FamilyDoctorID we can query.
                      if (fd.status === "Invited") return;
                      openDoctorPanel(fd.id);
                    }}
                    onDeactivate={(familyDoctorId) => {
                      if (actionBusy) return;
                      requestConfirm({
                        title: "Deactivate Doctor",
                        message: `Deactivate ${fd.displayName}? They will lose portal access to this clinic.`,
                        confirmLabel: "Deactivate",
                        danger: true,
                        onConfirm: () =>
                          runAction(
                            async () => {
                              await clinicAdminApi.deactivateFamilyDoctor(
                                familyDoctorId
                              );
                            },
                            "Family doctor deactivated."
                          ),
                      });
                    }}
                    onReactivate={(familyDoctorId) => {
                      if (actionBusy) return;
                      requestConfirm({
                        title: "Reactivate Doctor",
                        message: `Reactivate ${fd.displayName}? They will regain portal access to this clinic.`,
                        confirmLabel: "Reactivate",
                        onConfirm: () =>
                          runAction(
                            async () => {
                              await clinicAdminApi.reactivateFamilyDoctor(
                                familyDoctorId
                              );
                            },
                            "Family doctor reactivated."
                          ),
                      });
                    }}
                    onResendInvite={(providerInviteId) => {
                      if (actionBusy) return;
                      requestConfirm({
                        title: "Resend Invitation",
                        message: `Resend invitation to ${fd.displayName}?`,
                        confirmLabel: "Resend",
                        onConfirm: () =>
                          runAction(
                            async () => {
                              await clinicAdminApi.resendFamilyDoctorInvite(
                                providerInviteId
                              );
                            },
                            "Invitation resent."
                          ),
                      });
                    }}
                    onRevokeInvite={(providerInviteId) => {
                      if (actionBusy) return;
                      requestConfirm({
                        title: "Revoke Invitation",
                        message: `Revoke invitation for ${fd.displayName}? They will no longer be able to accept using the invite link.`,
                        confirmLabel: "Revoke",
                        danger: true,
                        onConfirm: () =>
                          runAction(
                            async () => {
                              await clinicAdminApi.revokeFamilyDoctorInvite(
                                providerInviteId
                              );
                            },
                            "Invitation revoked."
                          ),
                      });
                    }}
                  />
                ))}

                {familyDoctors.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty-state">
                      <div className="empty-state-icon">&#128101;</div>
                      <div>No family doctors added yet.</div>
                      <div className="empty-state-hint">
                        Click "+ Add Family Doctor" above to invite one.
                      </div>
                    </td>
                  </tr>
                )}
              </>
            )}
          </tbody>
        </table>
      </div>

      <AddFamilyDoctorModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onInvite={(provider: CpsLookupResult) => {
          if (actionBusy) return;

          const email = asString(provider.email);
          const cpso = asString(provider.cpscNumber);
          const fullName = asString(provider.displayName);
          const gender = asString((provider as any).gender);

          if (!email) {
            showToast("Email is required to send an invitation.", "error");
            return;
          }
          if (!cpso) {
            showToast("Missing CPSO number.", "error");
            return;
          }
          if (!fullName) {
            showToast("Doctor name is required.", "error");
            return;
          }
          if (gender !== "Male" && gender !== "Female") {
            showToast("Doctor gender is required.", "error");
            return;
          }

          requestConfirm({
            title: "Send Invitation",
            message: `Send invitation to ${fullName} (${email})?`,
            confirmLabel: "Send Invite",
            onConfirm: () =>
              runAction(
                async () => {
                  await clinicAdminApi.inviteFamilyDoctor({
                    cpso,
                    fullName,
                    email,
                    gender,
                  });
                  setShowAddModal(false);
                },
                "Invitation sent."
              ),
          });
        }}
      />

      {/* ===========================
          Right-side details panel
         =========================== */}
      {panel.open ? (
        <div className="modal-backdrop" onClick={closeDoctorPanel}>
          <div
            className="modal-card fd-detail-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h2>{panel.doctor?.fullName || "Family Doctor Details"}</h2>
              <button className="modal-close" onClick={closeDoctorPanel}>
                ×
              </button>
            </div>

            <div className="modal-body">
              {panel.loading ? (
                <div className="fd-panel-skeleton">
                  <Skeleton width="60%" height="14px" />
                  <Skeleton width="40%" height="14px" />
                  <Skeleton width="80%" height="14px" />
                </div>
              ) : panel.error ? (
                <div className="fd-panel-state fd-panel-error">
                  {panel.error}
                </div>
              ) : panel.doctor ? (
                <>
                  <div className="fd-panel-section">
                    <div className="fd-panel-section-title">Basics</div>

                    <div className="fd-grid">
                      <div className="fd-item">
                        <div className="fd-label">Name</div>
                        <div className="fd-value">
                          {panel.doctor.fullName || "—"}
                        </div>
                      </div>
                      <div className="fd-item">
                        <div className="fd-label">CPSO</div>
                        <div className="fd-value">
                          {panel.doctor.cpso || "—"}
                        </div>
                      </div>
                      <div className="fd-item">
                        <div className="fd-label">Email</div>
                        <div className="fd-value">
                          {panel.doctor.email || "—"}
                        </div>
                      </div>
                      <div className="fd-item">
                        <div className="fd-label">Gender</div>
                        <div className="fd-value">
                          {panel.doctor.gender || "—"}
                        </div>
                      </div>
                      <div className="fd-item">
                        <div className="fd-label">Status</div>
                        <div className="fd-value">
                          {panel.doctor.status || "—"}
                        </div>
                      </div>
                      <div className="fd-item">
                        <div className="fd-label">Activated</div>
                        <div className="fd-value">
                          {formatDateMaybe(
                            panel.doctor.activatedAt || undefined
                          )}
                        </div>
                      </div>
                      <div className="fd-item">
                        <div className="fd-label">Deactivated</div>
                        <div className="fd-value">
                          {formatDateMaybe(
                            panel.doctor.deactivatedAt || undefined
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="fd-panel-section">
                    <div className="fd-panel-section-title">
                      Accepting status
                    </div>

                    <div className="fd-grid">
                      <div className="fd-item">
                        <div className="fd-label">Accepting new patients</div>
                        <div className="fd-value">
                          {panel.doctor.acceptingNewPatients === true ? (
                            <span className="fd-accepting-badge fd-accepting-badge--yes">
                              Yes
                            </span>
                          ) : panel.doctor.acceptingNewPatients === false ? (
                            <span className="fd-accepting-badge fd-accepting-badge--no">
                              No
                            </span>
                          ) : (
                            "—"
                          )}
                        </div>
                      </div>
                      <div className="fd-item">
                        <div className="fd-label">Last confirmed</div>
                        <div className="fd-value">
                          {formatDateMaybe(
                            panel.doctor.acceptingStatusUpdatedAt || undefined
                          )}
                        </div>
                      </div>

                      {acceptingStatusHint(
                        panel.doctor.acceptingNewPatients,
                        panel.doctor.acceptingStatusUpdatedAt
                      ) ? (
                        <div className="fd-item">
                          <div className="fd-label">Confirmation status</div>
                          <div className="fd-value fd-muted">
                            {acceptingStatusHint(
                              panel.doctor.acceptingNewPatients,
                              panel.doctor.acceptingStatusUpdatedAt
                            )}
                          </div>
                        </div>
                      ) : null}

                      {panel.doctor.status === "Active" ? (
                        <div className="fd-item">
                          <div className="fd-label">Update accepting status</div>
                          <div className="fd-value">
                            <select
                              value={
                                acceptingStatusDraft === null
                                  ? ""
                                  : acceptingStatusDraft
                                  ? "true"
                                  : "false"
                              }
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "true") {
                                  setAcceptingStatusDraft(true);
                                } else if (value === "false") {
                                  setAcceptingStatusDraft(false);
                                } else {
                                  setAcceptingStatusDraft(null);
                                }
                              }}
                              disabled={actionBusy}
                            >
                              <option value="">Select status</option>
                              <option value="true">
                                Accepting new patients
                              </option>
                              <option value="false">
                                Not accepting new patients
                              </option>
                            </select>
                            <button
                              className="primary-btn"
                              onClick={() => updateAcceptingStatus()}
                              disabled={
                                actionBusy ||
                                acceptingStatusDraft === null ||
                                acceptingStatusDraft ===
                                  panel.doctor.acceptingNewPatients
                              }
                              title={actionBusy ? "Please wait…" : undefined}
                            >
                              {actionBusy ? "Updating…" : "Update"}
                            </button>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="fd-panel-section">
                    <div className="fd-panel-section-title">Patient intake</div>
                    <div className="fd-grid">
                      <div className="fd-item">
                        <div className="fd-label">Intake notes</div>
                        <div className="fd-notes">
                          {panel.doctor.intakeNotes || "—"}
                        </div>
                      </div>
                      <div className="fd-item">
                        <div className="fd-label">
                          First appointment booking URL
                        </div>
                        <div className="fd-value">
                          {bookingHref ? (
                            <a href={bookingHref} target="_blank" rel="noreferrer">
                              {bookingUrl}
                            </a>
                          ) : (
                            bookingUrl || "—"
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="fd-panel-section">
                    <div className="fd-panel-section-title">Capacity</div>
                    <div className="fd-grid">
                      <div className="fd-item">
                        <div className="fd-label">Declared capacity</div>
                        <div className="fd-value">
                          {formatCapacity(panel.doctor.declaredCapacity)}
                        </div>
                      </div>
                      <div className="fd-item">
                        <div className="fd-label">Remaining capacity override</div>
                        <div className="fd-value">
                          {formatCapacity(panel.doctor.remainingCapacityOverride)}
                        </div>
                      </div>
                      <div className="fd-item">
                        <div className="fd-label">
                          Remaining capacity last updated
                        </div>
                        <div className="fd-value">
                          {formatDateMaybe(
                            panel.doctor.remainingCapacityUpdatedAt || undefined
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="fd-panel-section">
                    <div className="fd-panel-section-title">Languages</div>
                    <div className="fd-chip-row">
                      {panel.languages.length ? (
                        panel.languages.map((x) => (
                          <span key={x.id} className="fd-chip">
                            {x.name}
                          </span>
                        ))
                      ) : (
                        <span className="fd-muted">—</span>
                      )}
                    </div>
                  </div>

                  <div className="fd-panel-section">
                    <div className="fd-panel-section-title">Visit types</div>
                    <div className="fd-chip-row">
                      {panel.visitTypes.length ? (
                        panel.visitTypes.map((x) => (
                          <span key={x.id} className="fd-chip">
                            {x.name}
                          </span>
                        ))
                      ) : (
                        <span className="fd-muted">—</span>
                      )}
                    </div>
                  </div>

                  <div className="fd-panel-section">
                    <div className="fd-panel-section-title">Coverage</div>
                    <div className="fd-chip-row">
                      {panel.coverageTypes.length ? (
                        panel.coverageTypes.map((x) => (
                          <span key={x.id} className="fd-chip">
                            {x.name}
                          </span>
                        ))
                      ) : (
                        <span className="fd-muted">—</span>
                      )}
                    </div>
                  </div>

                  <div className="fd-panel-section">
                    <div className="fd-panel-section-title">
                      Intake and matching preferences
                    </div>
                    {!intakePreferences.hasAny ? (
                      <div className="fd-muted">
                        No intake rules have been set yet.
                      </div>
                    ) : (
                      <div className="fd-grid">
                        <div className="fd-item">
                          <div className="fd-label">Age groups</div>
                          <div className="fd-value">
                            {intakePreferences.ageGroups.length ? (
                              intakePreferences.ageGroups.map((group) => (
                                <div key={group.key}>
                                  {group.label}: {group.preference}
                                </div>
                              ))
                            ) : (
                              <span className="fd-muted">No preference set.</span>
                            )}
                          </div>
                        </div>
                        <div className="fd-item">
                          <div className="fd-label">Complexity comfort</div>
                          <div className="fd-value">
                            {intakePreferences.complexity || "—"}
                          </div>
                        </div>
                        <div className="fd-item">
                          <div className="fd-label">Clinical comfort areas</div>
                          <div className="fd-chip-row">
                            {intakePreferences.clinicalAreas.length ? (
                              intakePreferences.clinicalAreas.map((label) => (
                                <span key={label} className="fd-chip">
                                  {label}
                                </span>
                              ))
                            ) : (
                              <span className="fd-muted">No preference set.</span>
                            )}
                          </div>
                        </div>
                        <div className="fd-item">
                          <div className="fd-label">Geography preference</div>
                          <div className="fd-value">
                            {intakePreferences.geography || "—"}
                          </div>
                        </div>
                        <div className="fd-item">
                          <div className="fd-label">Navigator note</div>
                          <div className="fd-notes">
                            {intakePreferences.navigatorNote || "—"}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div className="fd-panel-state">No details available.</div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <ConfirmDialog
        open={!!confirmState}
        title={confirmState?.title}
        message={confirmState?.message || ""}
        confirmLabel={confirmState?.confirmLabel}
        danger={confirmState?.danger}
        onConfirm={() => {
          confirmState?.onConfirm();
          setConfirmState(null);
        }}
        onCancel={() => setConfirmState(null)}
      />
    </div>
  );
}